import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

/**
 * AgentContext — single source of truth for the floating agent UX:
 *   - dock open/close state + the DOMRect of the orb at click time (so the
 *     dock knows where to anchor with flip-up fallback).
 *   - perfect-day celebration trigger, de-duplicated per local-day so any
 *     component (LogrosCard, AgentDock, IAPage, RoutinesSheet) can fire it
 *     without coordinating.
 *   - logros sync bus: when the agent dock mutates the day's logros via
 *     `/api/ia/interpret`, it dispatches the new items so LogrosCard
 *     refreshes instantly without refetching.
 */
const AgentContext = createContext(null);

export function AgentProvider({ children }) {
  const [dockState, setDockState] = useState({ open: false, anchor: null });
  const [perfectDay, setPerfectDay] = useState(null); // { ts, key } | null
  const celebratedKeyRef = useRef(null);
  const listenersRef = useRef(new Set());

  const openDock = useCallback((anchorRect) => {
    setDockState({ open: true, anchor: anchorRect || null });
  }, []);

  const closeDock = useCallback(() => {
    setDockState((s) => ({ ...s, open: false }));
  }, []);

  const triggerPerfectDay = useCallback((dateKey, totalLogros) => {
    // Idempotent within a mount: if the same date key already fired, ignore.
    const key = dateKey || new Date().toISOString().slice(0, 10);
    if (celebratedKeyRef.current === key) return;
    celebratedKeyRef.current = key;
    // Phase 10: the celebration banner + TTS render "{N} de {N} logros".
    // `total` is optional: callers that already know the array length pass
    // it in; otherwise the celebration falls back to non-dynamic copy.
    const total =
      typeof totalLogros === "number" && Number.isFinite(totalLogros) && totalLogros > 0
        ? totalLogros
        : null;
    setPerfectDay({ ts: Date.now(), key, total });
  }, []);

  const clearPerfectDay = useCallback(() => {
    setPerfectDay(null);
  }, []);

  /** Subscribe to "logros changed from outside" events (agent dock push). */
  const onLogrosSync = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  const pushLogrosSync = useCallback((logros) => {
    listenersRef.current.forEach((fn) => {
      try {
        fn(logros);
      } catch (e) {
        console.warn("logros sync listener failed", e);
      }
    });
  }, []);

  const value = useMemo(
    () => ({
      dockOpen: dockState.open,
      dockAnchor: dockState.anchor,
      openDock,
      closeDock,
      perfectDay,
      triggerPerfectDay,
      clearPerfectDay,
      onLogrosSync,
      pushLogrosSync,
    }),
    [
      dockState.open,
      dockState.anchor,
      perfectDay,
      openDock,
      closeDock,
      triggerPerfectDay,
      clearPerfectDay,
      onLogrosSync,
      pushLogrosSync,
    ],
  );

  return <AgentContext.Provider value={value}>{children}</AgentContext.Provider>;
}

export function useAgent() {
  const ctx = useContext(AgentContext);
  if (!ctx) throw new Error("useAgent must be used inside <AgentProvider>");
  return ctx;
}
